import { calculateDistance } from '../gpx/parse.js';

/**
 * Compare the recorded route (from FIT/GPX with timestamps) against a course GPX.
 * Returns a summary of deviations: distance delta, elevation delta, and per-section differences.
 *
 * @param {Array} actualTrackpoints - Recorded trackpoints (from FIT or GPX with timestamps)
 * @param {Array} courseTrackpoints - Course design trackpoints (from GPX)
 * @param {Array} actualSections - Sections computed from actual trackpoints
 * @param {Array} courseSections - Sections computed from course trackpoints
 * @returns {Object} Route deviation analysis
 */
export function analyzeRouteDeviation(actualTrackpoints, courseTrackpoints, actualSections, courseSections) {
  const actualTotalDist = lastDistance(actualTrackpoints);
  const courseTotalDist = lastDistance(courseTrackpoints);

  const distanceDelta = actualTotalDist - courseTotalDist;
  const distanceDeltaPct = courseTotalDist > 0 ? (distanceDelta / courseTotalDist) * 100 : 0;

  const actualElevGain = computeTotalElevGain(actualTrackpoints);
  const courseElevGain = computeTotalElevGain(courseTrackpoints);
  const elevGainDelta = actualElevGain - courseElevGain;

  const actualElevLoss = computeTotalElevLoss(actualTrackpoints);
  const courseElevLoss = computeTotalElevLoss(courseTrackpoints);
  const elevLossDelta = actualElevLoss - courseElevLoss;

  const avgDeviation = computeAverageDeviation(actualTrackpoints, courseTrackpoints);

  const sectionComparison = compareSections(actualSections, courseSections);

  return {
    actualDistance: actualTotalDist,
    courseDistance: courseTotalDist,
    distanceDelta,
    distanceDeltaPct,
    actualElevGain,
    courseElevGain,
    elevGainDelta,
    actualElevLoss,
    courseElevLoss,
    elevLossDelta,
    avgDeviationMeters: avgDeviation,
    sectionComparison
  };
}

function lastDistance(trackpoints) {
  return trackpoints.length > 0 ? trackpoints[trackpoints.length - 1].cumulativeDistance : 0;
}

function computeTotalElevGain(trackpoints) {
  let gain = 0;
  for (let i = 1; i < trackpoints.length; i++) {
    const diff = trackpoints[i].ele - trackpoints[i - 1].ele;
    if (diff > 0) gain += diff;
  }
  return gain;
}

function computeTotalElevLoss(trackpoints) {
  let loss = 0;
  for (let i = 1; i < trackpoints.length; i++) {
    const diff = trackpoints[i].ele - trackpoints[i - 1].ele;
    if (diff < 0) loss += Math.abs(diff);
  }
  return loss;
}

/**
 * Compute average lateral deviation between two routes by sampling actual points
 * and finding nearest course point.
 */
function computeAverageDeviation(actualTps, courseTps) {
  if (!actualTps.length || !courseTps.length) return 0;

  // Sample every N actual trackpoints for performance
  const sampleRate = Math.max(1, Math.floor(actualTps.length / 200));
  let totalDeviation = 0;
  let sampleCount = 0;

  for (let i = 0; i < actualTps.length; i += sampleRate) {
    const actual = actualTps[i];
    let minDist = Infinity;

    // Search in a windowed range around the expected position on the course
    const expectedFrac = actual.cumulativeDistance / (lastDistance(actualTps) || 1);
    const expectedIdx = Math.floor(expectedFrac * courseTps.length);
    const searchRadius = Math.max(50, Math.floor(courseTps.length * 0.05));
    const startIdx = Math.max(0, expectedIdx - searchRadius);
    const endIdx = Math.min(courseTps.length - 1, expectedIdx + searchRadius);

    for (let j = startIdx; j <= endIdx; j++) {
      const course = courseTps[j];
      const dist = calculateDistance(actual.lat, actual.lon, course.lat, course.lon);
      if (dist < minDist) minDist = dist;
    }

    totalDeviation += minDist;
    sampleCount++;
  }

  return sampleCount > 0 ? totalDeviation / sampleCount : 0;
}

/**
 * Compare sections count and distances. Basic comparison since sections may not match 1:1.
 */
function compareSections(actualSections, courseSections) {
  return {
    actualSectionCount: actualSections.length,
    courseSectionCount: courseSections.length,
    sectionCountDelta: actualSections.length - courseSections.length
  };
}
